import type { MaybeRefOrGetter } from 'vue';
import { computed, ref, toValue, watchEffect } from 'vue';
import type { UserHangarModule } from '~~/types';
import type {
  GameDataItemResponse,
  GameDataListResponse,
  GameVehicleDetail,
  GameVehicleImage,
  GameVehicleQuery,
  GameVehicleSummary,
  GameShipRating,
  GameShipCommercial,
  HangarEntry,
  LocalizedString,
  WikiItem,
  WikiVehicle,
  WikiVehicleFilters,
  WikiVehiclePort,
} from '~~/types/gamedata';
import { createShipIdResolver, type ShipVariantRef } from '~/utils/resolve-ship-id';

/**
 * Der Client wartet etwas länger als der Nitro-Upstream. So kann der Server
 * sauber antworten oder einen Retry auslösen, bevor der Browser die Verbindung
 * abbricht und ein anschließendes F5 nur den inzwischen warmen Cache trifft.
 */
const GAMEDATA_REQUEST_TIMEOUT_MS = 75_000;

/**
 * Zugriff auf die Star Citizen Wiki API.
 *
 * Alles läuft über den Nitro-Proxy `/api/gamedata` (siehe
 * `server/api/gamedata/[...].ts`) — nie direkt gegen api.star-citizen.wiki.
 */

/** Baut den Query-String in der Filter-Syntax der Wiki-API. */
export function buildVehicleQuery(query: GameVehicleQuery = {}): Record<string, string> {
  const params: Record<string, string> = {};

  for (const [key, value] of Object.entries(query.filter ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    // Kommaseparierte Werte sind bei den dokumentierten Feldern ein OR-Match.
    params[`filter[${key}]`] = Array.isArray(value) ? value.join(',') : String(value);
  }

  if (query.sort) params.sort = query.sort;
  if (query.include) params.include = query.include;
  if (query.version) params.version = query.version;
  if (query.page) params['page[number]'] = String(query.page);
  if (query.pageSize) params['page[size]'] = String(query.pageSize);

  return params;
}

/**
 * Lädt den schlanken Fahrzeug-Index — einmal pro App-Instanz.
 *
 * Über `useState` geteilt, damit Hangar, Flotte und Missionsplaner sich einen
 * Request teilen und das Ergebnis vom Server an den Client übertragen wird.
 * Der zusätzliche Promise-Guard verhindert parallele Requests, wenn mehrere
 * Komponenten im selben Tick starten.
 */
export function fetchVehicleIndex(): Promise<GameVehicleSummary[]> {
  const state = useState<GameVehicleSummary[] | null>('gamedata:vehicles', () => null);
  const nuxtApp = useNuxtApp();

  if (state.value) return Promise.resolve(state.value);

  const pending = nuxtApp._gamedataVehicleIndex as Promise<GameVehicleSummary[]> | undefined;
  if (pending) return pending;

  // Die Antwort ist 12 h browserseitig gecacht (`max-age`). Der Build-Commit im
  // Query macht die URL pro Deploy eindeutig — Änderungen am Index (z.B. Blacklist)
  // sind nach einem Deploy sofort sichtbar. In der Entwicklung (`DEV`) immer frisch.
  const { SOURCE_COMMIT } = useRuntimeConfig().public;
  const version = SOURCE_COMMIT === 'DEV' ? Date.now() : SOURCE_COMMIT;

  const request = $fetch<GameDataListResponse<GameVehicleSummary>>(
    '/api/gamedata/vehicles/summary',
    { query: { v: version } },
  )
    .then((response) => {
      state.value = response.data ?? [];
      return state.value;
    })
    .finally(() => {
      nuxtApp._gamedataVehicleIndex = undefined;
    });

  nuxtApp._gamedataVehicleIndex = request;
  return request;
}

/**
 * Der schlanke Fahrzeug-Index — Grundlage aller Listenansichten.
 *
 * Der Roh-Index der Wiki-API wiegt ~14 MB und kennt keine sparse fieldsets;
 * diese Projektion liegt bei ~344 KB für 334 Fahrzeuge.
 */
export function useVehicleIndex(options: { lazy?: boolean; server?: boolean } = {}) {
  return useAsyncData<GameVehicleSummary[]>('gamedata:vehicles', () => fetchVehicleIndex(), {
    default: () => [] as GameVehicleSummary[],
    lazy: options.lazy,
    server: options.server,
  });
}

/** Nachschlagetabelle Wiki-Slug → Fahrzeug. */
export function useVehicleMap() {
  const { data, pending, error, refresh } = useVehicleIndex();

  const map = computed(() => new Map((data.value ?? []).map((vehicle) => [vehicle.id, vehicle])));

  return { map, vehicles: data, pending, error, refresh };
}

/**
 * Löst ArisCorp-Schiffseinträge auf Wiki-Fahrzeuge auf.
 *
 * Nimmt `ship_id`, wenn gepflegt, und leitet sonst aus `external_refs` ab —
 * damit funktioniert das Frontend schon vor der Backend-Migration.
 */
export function useShipResolver() {
  const { map, vehicles, pending, error, refresh } = useVehicleMap();

  const resolveId = computed(() => createShipIdResolver(vehicles.value ?? []));

  /** Wiki-Fahrzeug zu einem ArisCorp-`ship_variants`-Eintrag. */
  function resolve(variant: ShipVariantRef | null | undefined): GameVehicleSummary | null {
    const id = resolveId.value(variant);
    return id ? map.value.get(id) ?? null : null;
  }

  /** Wiki-Fahrzeug direkt über den Slug. */
  function byId(id: string | null | undefined): GameVehicleSummary | null {
    return id ? map.value.get(id) ?? null : null;
  }

  return { resolve, byId, resolveId, map, vehicles, pending, error, refresh };
}

/**
 * Gefilterte Fahrzeugliste direkt von der Wiki-API.
 *
 * Für serverseitige Filter und Sortierung wie in der API-Doku. Achtung: die
 * Antwort ist gross (~47 KB pro Fahrzeug) — für reine Listenansichten ist
 * `useVehicleIndex()` fast immer die richtige Wahl.
 */
export function useVehicles(query: MaybeRefOrGetter<GameVehicleQuery> = {}) {
  const params = computed(() => buildVehicleQuery(toValue(query)));

  return useAsyncData<WikiVehicle[]>(
    () => `gamedata:vehicles:${JSON.stringify(params.value)}`,
    async () => {
      const response = await $fetch<GameDataListResponse<WikiVehicle>>('/api/gamedata/vehicles', {
        query: params.value,
      });
      return response.data ?? [];
    },
    {
      default: () => [] as WikiVehicle[],
      watch: [params],
    },
  );
}

/**
 * Volle Detaildaten eines Fahrzeugs.
 *
 * `id` akzeptiert alles, was die Wiki-Route versteht: Slug, Klassenname, UUID
 * oder Name. Für Konzeptschiffe fällt die API still auf den Ship-Matrix-Eintrag
 * zurück, es kommt also immer etwas Sinnvolles zurück.
 */
export function useVehicle(
  id: MaybeRefOrGetter<string | null | undefined>,
  options: { include?: string; version?: string; lazy?: boolean; server?: boolean } = {},
) {
  const identifier = computed(() => toValue(id));

  return useAsyncData<WikiVehicle | null>(
    () => `gamedata:vehicle:${identifier.value ?? 'none'}:${options.include ?? ''}`,
    async () => {
      if (!identifier.value) return null;

      const response = await $fetch<GameDataItemResponse<WikiVehicle>>(
        `/api/gamedata/vehicles/${encodeURIComponent(identifier.value)}`,
        {
          query: { include: options.include, version: options.version },
          timeout: GAMEDATA_REQUEST_TIMEOUT_MS,
        },
      );
      return response.data ?? null;
    },
    {
      default: () => null,
      lazy: options.lazy,
      server: options.server,
      watch: [identifier],
    },
  );
}

/**
 * ArisCorp-Bewertung eines Fahrzeugs aus dem CMS (`ship_ratings`) — `null`,
 * wenn (noch) keine gepflegt ist. Anders als `useVehicle()` kommt das nicht
 * von der Wiki-API, sondern aus Directus (siehe `/api/gamedata/ship-ratings`).
 */
export function useShipRating(id: MaybeRefOrGetter<string | null | undefined>) {
  const identifier = computed(() => toValue(id));

  return useAsyncData<GameShipRating | null>(
    () => `gamedata:ship-rating:${identifier.value ?? 'none'}`,
    async () => {
      if (!identifier.value) return null;

      const response = await $fetch<GameDataItemResponse<GameShipRating>>(
        `/api/gamedata/ship-ratings/${encodeURIComponent(identifier.value)}`,
      );
      return response.data ?? null;
    },
    {
      default: () => null,
      watch: [identifier],
    },
  );
}

/**
 * Commercial-Video eines Fahrzeugs aus Directus (`ship-commercials`-Ordner,
 * getaggt mit dem Wiki-Slug) — `null`, wenn (noch) keins hochgeladen ist.
 * Läuft wie `useShipRating()` über eine eigene Server-Route
 * (`/api/gamedata/ship-commercials`), nicht direkt gegen Directus: die
 * öffentliche Rolle hat auf `directus_files` typischerweise kein Leserecht,
 * die Route nutzt daher den statischen Server-Token.
 */
export function useShipCommercial(id: MaybeRefOrGetter<string | null | undefined>) {
  const identifier = computed(() => toValue(id));

  return useAsyncData<GameShipCommercial | null>(
    () => `gamedata:ship-commercial:${identifier.value ?? 'none'}`,
    async () => {
      if (!identifier.value) return null;

      const response = await $fetch<GameDataItemResponse<GameShipCommercial>>(
        `/api/gamedata/ship-commercials/${encodeURIComponent(identifier.value)}`,
      );
      return response.data ?? null;
    },
    {
      default: () => null,
      watch: [identifier],
    },
  );
}

/**
 * Fallback-Summary für Fahrzeuge, die nicht im (geblacklisteten)
 * Haupt-Fahrzeugindex stecken (Lackierungs-/Sondereditionen, siehe
 * `useVehiclePaints()`) — für deren eigene ShipExkurs-Detailseite, wenn sie
 * direkt von der "Lackierungen"-Sektion des Basisschiffs verlinkt wird.
 */
export function useVehicleFallbackSummary(
  id: MaybeRefOrGetter<string | null | undefined>,
  options: { lazy?: boolean; server?: boolean } = {},
) {
  const identifier = computed(() => toValue(id));

  return useAsyncData<GameVehicleSummary | null>(
    () => `gamedata:vehicle-fallback-summary:${identifier.value ?? 'none'}`,
    async () => {
      if (!identifier.value) return null;
      const response = await $fetch<GameDataItemResponse<GameVehicleSummary | null>>(
        `/api/gamedata/vehicles/${encodeURIComponent(identifier.value)}/summary`,
        { timeout: GAMEDATA_REQUEST_TIMEOUT_MS },
      );
      return response.data ?? null;
    },
    {
      default: () => null,
      lazy: options.lazy,
      server: options.server,
      watch: [identifier],
    },
  );
}

/**
 * Kaufbare Lackierungen eines Fahrzeugs (z.B. "Avenger Copernicus Livery"
 * für die Avenger-Baureihe) — für den "Lackierungen"-Abschnitt der
 * ShipExkurs-Detailseite. Die Fahrzeug-`port_tags` verweisen auch bei einer
 * Variante auf deren Basisbaureihe; der `class_name` dient als Rückfall.
 */
export function useVehiclePaints(
  className: MaybeRefOrGetter<string | null | undefined>,
  portTags: MaybeRefOrGetter<string[] | null | undefined>,
  options: { lazy?: boolean; server?: boolean } = {},
) {
  const identifier = computed(() => toValue(className));
  const vehicleTags = computed(() => toValue(portTags) ?? []);
  const cacheKey = computed(() => [identifier.value ?? 'none', ...vehicleTags.value].join(':'));

  return useAsyncData<WikiItem[]>(
    () => `gamedata:vehicle-paints:${cacheKey.value}`,
    async () => {
      if (!identifier.value && !vehicleTags.value.length) return [];
      const response = await $fetch<GameDataListResponse<WikiItem>>('/api/gamedata/vehicles/paints', {
        query: { className: identifier.value, portTags: vehicleTags.value },
        timeout: GAMEDATA_REQUEST_TIMEOUT_MS,
      });
      return response.data ?? [];
    },
    {
      default: () => [] as WikiItem[],
      lazy: options.lazy,
      server: options.server,
      watch: [identifier, vehicleTags],
    },
  );
}

/**
 * Bevorzugtes Angled-View-Bild eines Schiffs von FleetYards (für die
 * "Abmessungen"-Karte im ShipExkurs) — `null`, wenn FleetYards das Modell
 * nicht kennt. Läuft über `/api/gamedata/fleetyards`, FleetYards taucht nie
 * im Client auf.
 */
type FleetyardsVehicleReference = Pick<GameVehicleSummary, 'id' | 'name' | 'className' | 'matrixId'>;

export function useFleetyardsShipImage(
  vehicle: MaybeRefOrGetter<FleetyardsVehicleReference | null | undefined>,
) {
  const reference = computed(() => toValue(vehicle));

  return useAsyncData<string | null>(
    () => `gamedata:fleetyards-image:v3:${reference.value?.id ?? 'none'}`,
    async () => {
      if (!reference.value?.id) return null;

      const response = await $fetch<{ data: { image: string | null } }>(
        `/api/gamedata/fleetyards/${encodeURIComponent(reference.value.id)}`,
        {
          query: {
            resolverVersion: 3,
            name: reference.value.name,
            scIdentifier: reference.value.className ?? undefined,
            rsiId: reference.value.matrixId ?? undefined,
          },
        },
      );
      return response.data.image;
    },
    {
      default: () => null,
      watch: [reference],
    },
  );
}

/**
 * Lädt die tauschbaren Ports (Modul-Slots) eines Fahrzeugs, gecacht pro Slug.
 *
 * Nur `editable`-Ports mit mindestens einem `compatible_types`-Eintrag kommen
 * in Frage — die übrigen ~85% der Ports sind strukturelle Rumpfteile, die die
 * Wiki-API ebenfalls als "Port" modelliert. Diese Daten bleiben für die
 * Schiffsansicht verfügbar, sind aber ausdrücklich nicht der Modul-Editor im
 * Hangar (dort werden nur physische Schiffmodule verwendet).
 */
export function fetchVehiclePorts(slug: string): Promise<WikiVehiclePort[]> {
  const state = useState<Map<string, WikiVehiclePort[]>>('gamedata:vehicle-ports', () => new Map());
  const nuxtApp = useNuxtApp();

  if (state.value.has(slug)) return Promise.resolve(state.value.get(slug)!);

  const pendingMap = (nuxtApp._gamedataVehiclePorts ??= new Map<string, Promise<WikiVehiclePort[]>>()) as Map<
    string,
    Promise<WikiVehiclePort[]>
  >;
  const pending = pendingMap.get(slug);
  if (pending) return pending;

  const request = $fetch<GameDataItemResponse<WikiVehicle>>(
    `/api/gamedata/vehicles/${encodeURIComponent(slug)}`,
    {
      query: { include: 'ports' },
      timeout: GAMEDATA_REQUEST_TIMEOUT_MS,
    },
  )
    .then((response) => {
      const ports = (response.data?.ports ?? []).filter(
        (port) => port.editable && (port.compatible_types?.length ?? 0) > 0,
      );
      state.value.set(slug, ports);
      return ports;
    })
    .finally(() => {
      pendingMap.delete(slug);
    });

  pendingMap.set(slug, request);
  return request;
}

/**
 * Wie `fetchVehiclePorts()`, aber ohne den `editable`-Filter — für die
 * ShipExkurs-Detailseite, die auch strukturelle, nicht tauschbare Teile
 * anzeigt (Triebwerke, Treibstoff, Panzerung), die der Modul-Editor bewusst
 * ausblendet. Eigener Cache-Schlüssel, damit sich beide Aufrufer nicht
 * gegenseitig überschreiben.
 */
export function fetchAllVehiclePorts(slug: string): Promise<WikiVehiclePort[]> {
  const state = useState<Map<string, WikiVehiclePort[]>>('gamedata:vehicle-ports-all', () => new Map());
  const nuxtApp = useNuxtApp();

  if (state.value.has(slug)) return Promise.resolve(state.value.get(slug)!);

  const pendingMap = (nuxtApp._gamedataVehiclePortsAll ??= new Map<string, Promise<WikiVehiclePort[]>>()) as Map<
    string,
    Promise<WikiVehiclePort[]>
  >;
  const pending = pendingMap.get(slug);
  if (pending) return pending;

  const request = $fetch<GameDataItemResponse<WikiVehicle>>(
    `/api/gamedata/vehicles/${encodeURIComponent(slug)}`,
    {
      query: { include: 'ports' },
      timeout: GAMEDATA_REQUEST_TIMEOUT_MS,
    },
  )
    .then((response) => {
      const ports = response.data?.ports ?? [];
      state.value.set(slug, ports);
      return ports;
    })
    .finally(() => {
      pendingMap.delete(slug);
    });

  pendingMap.set(slug, request);
  return request;
}

/**
 * Lädt Items eines Ausrüstungstyps (`compatible_types[].type`, z.B. `Cooler`),
 * gecacht pro Typ.
 *
 * Bewusst kein globaler Item-Index — die Wiki-API kennt keine sparse
 * fieldsets für Items (jedes Item wiegt mehrere KB), und der volle Katalog
 * hat >12.000 Einträge. `page[size]: 50` deckt praktisch jeden Ausrüstungstyp
 * ausreichend für die Browse-Ansicht ab; echte Suche ist ein späteres Ziel.
 */
export function fetchItemsByType(type: string): Promise<WikiItem[]> {
  const state = useState<Map<string, WikiItem[]>>('gamedata:items-by-type', () => new Map());
  const nuxtApp = useNuxtApp();

  if (state.value.has(type)) return Promise.resolve(state.value.get(type)!);

  const pendingMap = (nuxtApp._gamedataItemsByType ??= new Map<string, Promise<WikiItem[]>>()) as Map<
    string,
    Promise<WikiItem[]>
  >;
  const pending = pendingMap.get(type);
  if (pending) return pending;

  const request = $fetch<GameDataListResponse<WikiItem>>('/api/gamedata/items', {
    query: { 'filter[type]': type, 'page[size]': 50 },
  })
    .then((response) => {
      const items = response.data ?? [];
      state.value.set(type, items);
      return items;
    })
    .finally(() => {
      pendingMap.delete(type);
    });

  pendingMap.set(type, request);
  return request;
}

/** Deutsch mit Fallback Englisch — Client-Pendant zu `server/utils/gamedata.ts:localized()`. */
export function localizeText(value: LocalizedString | string | null | undefined): string | null {
  if (value == null) return null;
  if (typeof value === 'string') return value || null;
  return value.de_DE || value.en_EN || null;
}

/** Reduziert die Wiki-Vollantwort auf die im Hero benötigten Text- und Bilddaten. */
export function toVehicleDetail(raw: WikiVehicle | null | undefined): GameVehicleDetail {
  return {
    description: localizeText(raw?.description),
    productionNote: localizeText(raw?.production_note),
    images: (raw?.images ?? [])
      .map((image): GameVehicleImage | null => {
        const original = image.original_url ?? image.thumbnail_url;
        if (!original) return null;
        return {
          url: original,
          thumbnail: image.thumbnail_url ?? original,
          width: image.thumbnail_width ?? image.original_width ?? null,
          height: image.thumbnail_height ?? image.original_height ?? null,
        };
      })
      .filter((image): image is GameVehicleImage => image !== null),
  };
}

/**
 * Ergänzende Detaildaten eines Fahrzeugs (Beschreibung, Galerie) für die
 * Quick-View, gecacht pro Slug.
 *
 * Bewusst kein weiteres `GameVehicleSummary`-Duplikat — Crew/Cargo/Speed/Maße
 * etc. sind dort bereits vorhanden und werden hier nicht erneut geladen.
 */
export function fetchVehicleDetail(slug: string): Promise<GameVehicleDetail> {
  const state = useState<Map<string, GameVehicleDetail>>('gamedata:vehicle-detail', () => new Map());
  const nuxtApp = useNuxtApp();

  if (state.value.has(slug)) return Promise.resolve(state.value.get(slug)!);

  const pendingMap = (nuxtApp._gamedataVehicleDetail ??= new Map<string, Promise<GameVehicleDetail>>()) as Map<
    string,
    Promise<GameVehicleDetail>
  >;
  const pending = pendingMap.get(slug);
  if (pending) return pending;

  const request = $fetch<GameDataItemResponse<WikiVehicle>>(
    `/api/gamedata/vehicles/${encodeURIComponent(slug)}`,
    { timeout: GAMEDATA_REQUEST_TIMEOUT_MS },
  )
    .then((response) => {
      const detail = toVehicleDetail(response.data);
      state.value.set(slug, detail);
      return detail;
    })
    .finally(() => {
      pendingMap.delete(slug);
    });

  pendingMap.set(slug, request);
  return request;
}

/**
 * Volle Wiki-Rohantwort (inkl. `ports`) eines Fahrzeugs, gecacht pro Slug —
 * für die Schiffsvergleichsseite (`shipexkurs/vergleich.vue`), die eine zur
 * Laufzeit wachsende/schrumpfende Anzahl Schiffe lädt und dafür nicht wie die
 * Detailseite (`ships/[id].vue`) für jedes Schiff einen eigenen
 * `useVehicle()`/`useAsyncData()`-Aufruf anlegen kann (deren Anzahl müsste
 * über Renders hinweg stabil bleiben). Anders als `fetchVehicleDetail()` wird
 * hier bewusst die volle (~47 KB) Rohantwort gehalten statt nur Text/Galerie,
 * da die Vergleichsseite auch Kennzahlen (`weaponry`, `shield`, `armor`,
 * `agility`, ...) und Ports braucht.
 */
export function fetchVehicleRaw(slug: string): Promise<WikiVehicle | null> {
  const state = useState<Map<string, WikiVehicle>>('gamedata:vehicle-raw', () => new Map());
  const nuxtApp = useNuxtApp();

  if (state.value.has(slug)) return Promise.resolve(state.value.get(slug)!);

  const pendingMap = (nuxtApp._gamedataVehicleRaw ??= new Map<string, Promise<WikiVehicle | null>>()) as Map<
    string,
    Promise<WikiVehicle | null>
  >;
  const pending = pendingMap.get(slug);
  if (pending) return pending;

  const request = $fetch<GameDataItemResponse<WikiVehicle>>(
    `/api/gamedata/vehicles/${encodeURIComponent(slug)}`,
    { query: { include: 'ports' }, timeout: GAMEDATA_REQUEST_TIMEOUT_MS },
  )
    .then((response) => {
      const raw = response.data ?? null;
      if (raw) state.value.set(slug, raw);
      return raw;
    })
    .catch(() => null)
    .finally(() => {
      pendingMap.delete(slug);
    });

  pendingMap.set(slug, request);
  return request;
}

/** Einzelnes Item zur Anzeige-Auflösung (z.B. `wiki_item_slug` im Hangar), gecacht pro Slug. */
export function fetchItem(slug: string): Promise<WikiItem | null> {
  const state = useState<Map<string, WikiItem>>('gamedata:item', () => new Map());
  const nuxtApp = useNuxtApp();

  if (state.value.has(slug)) return Promise.resolve(state.value.get(slug)!);

  const pendingMap = (nuxtApp._gamedataItem ??= new Map<string, Promise<WikiItem | null>>()) as Map<
    string,
    Promise<WikiItem | null>
  >;
  const pending = pendingMap.get(slug);
  if (pending) return pending;

  const request = $fetch<GameDataItemResponse<WikiItem>>(`/api/gamedata/items/${encodeURIComponent(slug)}`)
    .then((response) => {
      const item = response.data ?? null;
      if (item) state.value.set(slug, item);
      return item;
    })
    .catch(() => null)
    .finally(() => {
      pendingMap.delete(slug);
    });

  pendingMap.set(slug, request);
  return request;
}

/**
 * Volldaten mehrerer Items parallel laden (z.B. alle `equipped_item`s der
 * Ports eines Fahrzeugs für die ShipExkurs-Komponentenübersicht).
 *
 * `filter[uuid]=a,b,c` ist trotz Komma-OR-Syntax bei anderen Feldern **kein**
 * unterstütztes Filterfeld der Wiki-API (live geprüft: die API ignoriert es
 * still und liefert die ungefilterte Standardseite zurück). Statt dafür
 * einzelne `fetchItem()`-Requests parallel vom Browser zu stellen (bei
 * komplexen Schiffen mehrere hundert Requests für einen einzigen Seitenaufruf),
 * läuft die Auflösung über `/api/gamedata/items/batch` — ein einzelner
 * Client-Request, die Wiki-API-Einzel-Fetches passieren serverseitig
 * (`getItemsByUuids()`, dort weiterhin pro UUID gecacht). Der lokale Cache
 * (`gamedata:item`) ist derselbe wie bei `fetchItem()`, geteilte Komponenten
 * über mehrere Schiffe hinweg lösen also weiterhin ohne Netzwerk-Roundtrip auf.
 */
export async function fetchItemsByUuid(uuids: string[]): Promise<Map<string, WikiItem>> {
  const unique = [...new Set(uuids)];
  const result = new Map<string, WikiItem>();
  if (!unique.length) return result;

  const state = useState<Map<string, WikiItem>>('gamedata:item', () => new Map());
  const nuxtApp = useNuxtApp();
  const pendingMap = (nuxtApp._gamedataItem ??= new Map<string, Promise<WikiItem | null>>()) as Map<
    string,
    Promise<WikiItem | null>
  >;

  const missing: string[] = [];
  for (const uuid of unique) {
    const cached = state.value.get(uuid);
    if (cached) result.set(uuid, cached);
    else missing.push(uuid);
  }
  if (!missing.length) return result;

  const toFetch = missing.filter((uuid) => !pendingMap.has(uuid));

  if (toFetch.length) {
    const request = $fetch<GameDataListResponse<WikiItem>>('/api/gamedata/items/batch', {
      method: 'POST',
      body: { uuids: toFetch },
      timeout: GAMEDATA_REQUEST_TIMEOUT_MS,
    })
      .then((response) => {
        for (const item of response.data ?? []) {
          state.value.set(item.uuid, item);
        }
      })
      .finally(() => {
        for (const uuid of toFetch) pendingMap.delete(uuid);
      });

    for (const uuid of toFetch) {
      pendingMap.set(uuid, request.then(() => state.value.get(uuid) ?? null));
    }
  }

  await Promise.all(missing.map((uuid) => pendingMap.get(uuid) ?? Promise.resolve(state.value.get(uuid) ?? null)));

  for (const uuid of missing) {
    const item = state.value.get(uuid);
    if (item) result.set(uuid, item);
  }
  return result;
}

/** Anzeige-Form einer echten Schiffmodul-Zuweisung. */
export interface AssignedModuleDisplay {
  id: string;
  name: string;
  /** Slot-Name (Alt-Format) bzw. Wiki-Port-Key (Neu-Format), z.B. `hardpoint_cooler_right`. */
  slotName: string | null;
}

/**
 * Löst ausschließlich Wiki-Items des Typs `Module` für die Anzeige auf.
 * Komponenten an Wiki-Ports (Waffen, Cooler etc.) werden ignoriert.
 */
export function useAssignedModules(entries: MaybeRefOrGetter<HangarEntry[]>) {
  const itemsMap = ref<Map<string, WikiItem>>(new Map());

  watchEffect(async () => {
    const missingSlugs = new Set<string>();

    for (const entry of toValue(entries)) {
      for (const assignment of (entry.modules ?? []) as UserHangarModule[]) {
        if (assignment.wiki_item_slug && !itemsMap.value.has(assignment.wiki_item_slug)) {
          missingSlugs.add(assignment.wiki_item_slug);
        }
      }
    }

    if (!missingSlugs.size) return;

    const slugs = [...missingSlugs];
    const results = await Promise.all(slugs.map((slug) => fetchItem(slug)));
    const next = new Map(itemsMap.value);
    slugs.forEach((slug, index) => {
      const item = results[index];
      if (item?.type === 'Module') next.set(slug, item);
    });
    itemsMap.value = next;
  });

  function assignedModules(item: HangarEntry): AssignedModuleDisplay[] {
    return ((item.modules ?? []) as UserHangarModule[])
      .map((assignment): AssignedModuleDisplay | null => {
        if (assignment.wiki_item_slug) {
          const wikiItem = itemsMap.value.get(assignment.wiki_item_slug);
          if (!wikiItem) return null;
          return {
            id: assignment.wiki_item_slug,
            name: wikiItem.name,
            slotName: assignment.wiki_port ?? null,
          };
        }
        return null;
      })
      .filter((entry): entry is AssignedModuleDisplay => entry !== null);
  }

  return { assignedModules, itemsMap };
}

/**
 * Kanonische Filter-Definitionen der Wiki-API (`/api/vehicles/filters`) —
 * welche Werte für `manufacturer`, `size`, `career`, `role` etc. tatsächlich
 * vorkommen, inklusive Trefferzahl. Grundlage für Filter-UIs, statt Optionen
 * aus einem bereits geladenen (u.U. unvollständigen) Index abzuleiten.
 */
export function useVehicleFilters() {
  return useAsyncData(
    'gamedata:vehicle-filters',
    async () => {
      const response = await $fetch<{ filters: WikiVehicleFilters }>('/api/gamedata/vehicles/filters');
      return response.filters;
    },
    { default: () => ({}) as WikiVehicleFilters },
  );
}

/** Herstellerliste der Wiki-API (nicht die ArisCorp-`companies`). */
export function useGameManufacturers() {
  return useAsyncData(
    'gamedata:manufacturers',
    async () => {
      const response = await $fetch<GameDataListResponse<{ name: string; code: string }>>(
        '/api/gamedata/manufacturers',
        { query: { 'page[size]': 200 } },
      );
      return response.data ?? [];
    },
    { default: () => [] },
  );
}
